// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { FunctionComponent, PropsWithChildren, ReactElement, useEffect, useState } from "react";
import Search from "../../components/Search";
import { Grid, Paper, Typography } from "@mui/material";
import CaseCard from "../../components/CaseStateCard";
import { RESOURCE_SERVER_URLS } from "../../data/configs/spl-config";
import { useGetApi } from "../../data/hooks/api";
import { caseDetailsWithCount } from "../../data/utils/types";
import CaseStateView from "../../components/CaseStateView";

interface CasesPropsInterface {
}

export const Cases: FunctionComponent<PropsWithChildren<CasesPropsInterface>> = (
): ReactElement => {

    const [showTable, setShowTable] = useState(true);
    const [caseState, setCaseState] = useState("");
    const [page1, setPage1] = useState(0);
    const [page2, setPage2] = useState(0);
    const [page3, setPage3] = useState(0);
    const [page4, setPage4] = useState(0);
    const [page5, setPage5] = useState(0);
    const [page6, setPage6] = useState(0);
    const [rowsPerPage1, setRowsPerPage1] = useState(10);
    const [rowsPerPage2, setRowsPerPage2] = useState(10);
    const [rowsPerPage3, setRowsPerPage3] = useState(10);
    const [rowsPerPage4, setRowsPerPage4] = useState(10);
    const [rowsPerPage5, setRowsPerPage5] = useState(10);
    const [rowsPerPage6, setRowsPerPage6] = useState(10);

    // Only the 6 main case states that are actively displayed in the dashboard
    const states: string[] = ['Open', 'Work In Progress', 'Awaiting Info', 'Solution Proposed', 'Waiting on WSO2', 'Reopened'];

    // API hooks with static URLs to prevent race conditions during pagination changes
    const { data: data1, loading: loading1, error: error1, getApiData: getApiData1 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Open&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });
    const { data: data2, loading: loading2, error: error2, getApiData: getApiData2 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Work%20In%20Progress&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });
    const { data: data3, loading: loading3, error: error3, getApiData: getApiData3 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Awaiting%20Info&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });
    const { data: data4, loading: loading4, error: error4, getApiData: getApiData4 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Solution%20Proposed&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });
    const { data: data5, loading: loading5, error: error5, getApiData: getApiData5 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Waiting%20on%20WSO2&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });
    const { data: data6, loading: loading6, error: error6, getApiData: getApiData6 } = useGetApi<caseDetailsWithCount>({
        url: `${RESOURCE_SERVER_URLS[0]}/cases?stateFilter=Reopened&offset=0&limit=10`,
        headers: { "accept": "application/json" }
    });

    // Build API URLs with proper pagination parameters
    const apiUrl1 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[0]}&offset=${page1 * rowsPerPage1}&limit=${rowsPerPage1}`;
    const apiUrl2 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[1]}&offset=${page2 * rowsPerPage2}&limit=${rowsPerPage2}`;
    const apiUrl3 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[2]}&offset=${page3 * rowsPerPage3}&limit=${rowsPerPage3}`;
    const apiUrl4 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[3]}&offset=${page4 * rowsPerPage4}&limit=${rowsPerPage4}`;
    const apiUrl5 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[4]}&offset=${page5 * rowsPerPage5}&limit=${rowsPerPage5}`;
    const apiUrl6 = RESOURCE_SERVER_URLS[0] + `/cases?stateFilter=${states[5]}&offset=${page6 * rowsPerPage6}&limit=${rowsPerPage6}`;

    // Load data when pagination changes - using manual getApiData calls to prevent race conditions
    useEffect(() => {
        getApiData1(apiUrl1);
    }, [rowsPerPage1, page1]);
    useEffect(() => {
        getApiData2(apiUrl2);
    }, [rowsPerPage2, page2]);
    useEffect(() => {
        getApiData3(apiUrl3);
    }, [rowsPerPage3, page3]);
    useEffect(() => {
        getApiData4(apiUrl4);
    }, [rowsPerPage4, page4]);
    useEffect(() => {
        getApiData5(apiUrl5);
    }, [rowsPerPage5, page5]);
    useEffect(() => {
        getApiData6(apiUrl6);
    }, [rowsPerPage6, page6]);

    return (
        <>
            <Search searchOption={"case"} setShowTable={setShowTable} />
            {caseState === "" && showTable &&
                <Grid container p={2} spacing={3} sx={{ height: "85%" }}>
                    <Grid item xs={12} sx={{ height: "100%" }}>
                        <Grid container>
                            <Grid item xs={1.5}></Grid>
                            <Grid item xs={9} sx={{ display: "flex", justifyContent: "center" }}>
                                <Paper sx={{ backgroundColor: "#ECECEC" }}>
                                    <Grid container p={3}>
                                        <Grid item xs={12} sx={{ display: "flex" }}>
                                            <Typography align="left" gutterBottom variant="h4">
                                                Overall Case Summary
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data1} loading={loading1} error={error1} state={states[0]} setCaseState={setCaseState} color="#0052cc" />
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data2} loading={loading2} error={error2} state={states[1]} setCaseState={setCaseState} color="#008000" />
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data3} loading={loading3} error={error3} state={states[2]} setCaseState={setCaseState} color="pink" />
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data4} loading={loading4} error={error4} state={states[3]} setCaseState={setCaseState} color="brown" />
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data5} loading={loading5} error={error5} state={states[4]} setCaseState={setCaseState} color="#F25C36" />
                                        </Grid>
                                        <Grid item xs={4} p={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <CaseCard data={data6} loading={loading6} error={error6} state={states[5]} setCaseState={setCaseState} color="#8993a4" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                            <Grid item xs={1.5}></Grid>
                        </Grid>
                    </Grid>
                </Grid>
            }
            {caseState === states[0] && showTable && data1 &&
                <CaseStateView data={data1} loading={loading1} error={error1} state={states[0]} setCaseState={setCaseState} page={page1}
                    setPage={setPage1} rowsPerPage={rowsPerPage1} setRowsPerPage={setRowsPerPage1} />
            }
            {caseState === states[1] && showTable && data2 &&
                <CaseStateView data={data2} loading={loading2} error={error2} state={states[1]} setCaseState={setCaseState} page={page2}
                    setPage={setPage2} rowsPerPage={rowsPerPage2} setRowsPerPage={setRowsPerPage2} />
            }
            {caseState === states[2] && showTable && data3 &&
                <CaseStateView data={data3} loading={loading3} error={error3} state={states[2]} setCaseState={setCaseState} page={page3}
                    setPage={setPage3} rowsPerPage={rowsPerPage3} setRowsPerPage={setRowsPerPage3} />
            }
            {caseState === states[3] && showTable && data4 &&
                <CaseStateView data={data4} loading={loading4} error={error4} state={states[3]} setCaseState={setCaseState} page={page4}
                    setPage={setPage4} rowsPerPage={rowsPerPage4} setRowsPerPage={setRowsPerPage4} />
            }
            {caseState === states[4] && showTable && data5 &&
                <CaseStateView data={data5} loading={loading5} error={error5} state={states[4]} setCaseState={setCaseState} page={page5}
                    setPage={setPage5} rowsPerPage={rowsPerPage5} setRowsPerPage={setRowsPerPage5} />
            }
            {caseState === states[5] && showTable && data6 &&
                <CaseStateView data={data6} loading={loading6} error={error6} state={states[5]} setCaseState={setCaseState} page={page6}
                    setPage={setPage6} rowsPerPage={rowsPerPage6} setRowsPerPage={setRowsPerPage6} />
            }
        </>
    );
}
